import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';
import {FmlxSwitch, FmlxTextBox, FmlxLabel, FmlxTooltip, FmlxIcon} from 'fmlx-common-ui';
import {getInstrumentStatus} from 'stores/business/instrumentBusiness';
import {CurrentStateEnum, ViewMode} from 'constants/dataEnum';

import './HumiditySwitch.scss';

const MIN = 35;
const MAX = 85;

function HumiditySwitch({
  enabled,
  value,
  onChangeEnabled,
  onChangeValue,
  onErrorChange,
  showInfoIcon,
  isReadOnly,
  handleCanProceed,
}) {
  const {formatMessage} = useIntl();
  const dispatch = useDispatch();

  const [switchStatus, setSwitchStatus] = useState(enabled);
  const [inputValue, setInputValue] = useState(value || '');
  const [error, setError] = useState('');
  const [disabled, setDisabled] = useState(isReadOnly);
  const appStateViewMode = useSelector((state) => state.appState?.viewMode);

  const textBox = React.useRef(null);
  const isInsideTaskList = !showInfoIcon;

  useEffect(() => {
    if (switchStatus) textBox?.current.focus();
  }, [switchStatus]);

  useEffect(() => {
    const fetchInstrumentStatus = async () => {
      const instrumentStatus = await dispatch(getInstrumentStatus());

      const disableSwitch =
        isReadOnly ||
        instrumentStatus?.currentState !== CurrentStateEnum.IDLE ||
        (isInsideTaskList && appStateViewMode === ViewMode.RUNTIME);

      setDisabled(disableSwitch);
    };

    fetchInstrumentStatus();
  }, [isInsideTaskList, appStateViewMode, isReadOnly, dispatch]);

  useEffect(() => {
    if (!handleCanProceed) return;

    const hasChanged = switchStatus !== enabled || inputValue !== value;
    const canProceed = error === '' && !disabled && hasChanged;
    handleCanProceed(canProceed);
  }, [switchStatus, inputValue, error, disabled, handleCanProceed, enabled, value]);

  const handleSwitchChange = () => {
    const newStatus = !switchStatus;
    setSwitchStatus(newStatus);
    onChangeEnabled(newStatus);
    handleErrorChange('');
  };

  const handleInputChange = (e) => {
    const val = e?.value ?? '';
    setInputValue(val.toString());
  };

  const handleErrorChange = (errorMessage) => {
    setError(errorMessage);
    if (onErrorChange) onErrorChange(errorMessage);
  };

  const handleBlur = () => {
    const trimmed = inputValue.trim();
    const parsed = Number(trimmed);
    let errorMessage = '';

    if (!trimmed) {
      errorMessage = formatMessage({id: 'humiditySwitch.tooltip.errorMessage.cannotBeEmpty'});
    } else if (Number.isNaN(parsed) || parsed < MIN || parsed > MAX) {
      errorMessage = formatMessage(
        {id: 'humiditySwitch.tooltip.errorMessage.mustBeBetweenRange'},
        {minValue: MIN, maxValue: MAX},
      );
    } else {
      onChangeValue(trimmed);
    }

    handleErrorChange(errorMessage);
  };

  return (
    <div className="humidity-switch">
      <FmlxLabel size={FmlxLabel.Size.SM} weight={FmlxLabel.Weight.BOLD} className="humidity-switch__label">
        Humidity
      </FmlxLabel>
      <FmlxSwitch
        size={FmlxSwitch.Size.SMALL}
        checked={switchStatus}
        onChange={handleSwitchChange}
        className="humidity-switch__toggle"
        disabled={disabled}
      />
      {switchStatus && (
        <div className="humidity-switch__input-wrapper">
          <div className={`humidity-switch__input-container ${error ? 'has-error' : ''}`}>
            <FmlxTextBox
              className="humidity-switch__input"
              mode={FmlxTextBox.Mode.NUMBER}
              size={FmlxTextBox.Size.SMALL}
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleBlur}
              minValue={MIN}
              maxValue={MAX}
              ref={textBox}
              disabled={disabled}
            />
            <span className="humidity-switch__suffix">%</span>
          </div>

          {error && (
            <FmlxTooltip title={error} placement={FmlxTooltip.Placement.BOTTOM}>
              <div>
                <FmlxIcon
                  name="Warning"
                  size={FmlxIcon.Size.SMALL}
                  customColor="#FF5252"
                  className="humidity-switch__icon"
                />
              </div>
            </FmlxTooltip>
          )}
        </div>
      )}
      {showInfoIcon && disabled && (
        <FmlxTooltip
          title={formatMessage({id: 'humiditySwitch.tooltip.protocolRunning'})}
          placement={FmlxTooltip.Placement.TOP}
        >
          <div>
            <FmlxIcon name="Info" size={FmlxIcon.Size.SMALL} customColor="#006AF5" className="humidity-switch__icon" />
          </div>
        </FmlxTooltip>
      )}
    </div>
  );
}

HumiditySwitch.propTypes = {
  enabled: PropTypes.bool.isRequired,
  value: PropTypes.string.isRequired,
  onChangeEnabled: PropTypes.func.isRequired,
  onChangeValue: PropTypes.func.isRequired,
  onErrorChange: PropTypes.func,
  showInfoIcon: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  handleCanProceed: PropTypes.func,
};

HumiditySwitch.defaultProps = {
  onErrorChange: undefined,
  showInfoIcon: false,
  isReadOnly: false,
  handleCanProceed: undefined,
};

export default HumiditySwitch;
